import {
  components as RSComponents,
  type GroupBase,
  type OptionProps,
  type DropdownIndicatorProps,
  type MultiValueProps,
  type PlaceholderProps,
} from 'react-select';
import clsx from 'clsx';
import { Checkbox } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import SearchIcon from '~/shared/assets/icons/search.svg';
import CheckIcon from '~/shared/assets/icons/check.svg';

export type OptionType = { value: string; label: string };

export function DropdownIndicator(
  props: DropdownIndicatorProps<OptionType, true, GroupBase<OptionType>>
) {
  const isOpen = props.selectProps.menuIsOpen;
  const isDisabled = props.selectProps.isDisabled as boolean;
  return (
    <RSComponents.DropdownIndicator {...props}>
      <ChevronDownIcon
        className={clsx(
          'w-[14px] h-[14px] stroke-[3] transition-transform duration-150',
          isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-primary-900 cursor-pointer',
          isOpen && !isDisabled && 'rotate-180'
        )}
      />
    </RSComponents.DropdownIndicator>
  );
}

export function Option(props: OptionProps<OptionType, true, GroupBase<OptionType>>) {
  const { isSelected, label, innerProps, innerRef, isDisabled } = props;
  const { ...restInnerProps } = innerProps as {
    className?: string;
    style?: React.CSSProperties;
    [key: string]: unknown;
  };

  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (isDisabled) {
      return;
    }
    if (isSelected) {
      e.preventDefault();
      e.stopPropagation();
      const selected = (props.selectProps.value ?? []) as OptionType[];
      const next = selected.filter((v) => v.value !== (props.data as OptionType).value);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (props.selectProps as any).onChange?.(next, {
        action: 'deselect-option',
        option: props.data,
      });
      return;
    }
    // fall back to default behavior for selecting
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (restInnerProps as any).onMouseDown?.(e);
  };

  return (
    <div
      ref={innerRef}
      {...restInnerProps}
      onMouseDown={handleMouseDown}
      className={clsx(
        'text-sm leading-6 w-full h-12 2xl:h-14 flex items-center justify-between px-5 py-[18px] border-b border-gray-200 first:rounded-t-lg last:rounded-b-lg group',
        isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-20 cursor-pointer'
      )}
    >
      <div className="flex items-center gap-3">
        <Checkbox
          checked={isSelected}
          className="group block size-5 p-[4px] rounded-md border border-primary-100 bg-white data-checked:bg-primary-500 ring-0 active:ring-primary-300 focus:ring-primary-500 cursor-pointer"
        >
          <CheckIcon className="text-white stroke-2 size-2.5 opacity-0 group-data-checked:opacity-100 transition duration-200 ease-in-out" />
        </Checkbox>
        <span className="text-primary-900">{label}</span>
      </div>
    </div>
  );
}

export function MultiValue(props: MultiValueProps<OptionType, true, GroupBase<OptionType>>) {
  const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Call react-select's internal remove handler
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props as any).removeProps?.onClick?.(e);
  };
  return <RSComponents.MultiValue {...props} innerProps={{ ...props.innerProps, onMouseDown }} />;
}

export function Placeholder(props: PlaceholderProps<OptionType, true, GroupBase<OptionType>>) {
  return (
    <RSComponents.Placeholder {...props}>
      <div className="flex items-center gap-2">
        <SearchIcon className="w-5 h-5 stroke-[2] text-gray-400" />
        <span>{props.children}</span>
      </div>
    </RSComponents.Placeholder>
  );
}

export function NoOptionsMessage({
  inputValue,
  isLoading,
}: {
  inputValue: string;
  isLoading?: boolean;
}) {
  if (isLoading || !inputValue.trim()) {
    return null;
  }
  return <p className="text-sm text-gray-500 px-5 py-4 text-center">No results found</p>;
}
